import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {JobFeed, NavLayout, ResponsiveLayout} from '@layouts';
import {
  getConfigByApp,
  getKeyFromPath,
  getLocationIds,
  isUserAgentForBot,
  utilServerSideDeviceDetection,
} from '@utils';
import {metaInfo} from '@configs';
import {COURSE, JOBFEED} from '@constants';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {SeoLayout} from '@layouts/SeoLayout';
import {cleanUrl} from '@jobseeker/lotus/utils';
import type {GetServerSidePropsContext} from 'next/types';
import {Chat, InjectSearchOverlay} from '@components';
import courseService from '@jobseeker/common-core/services/course.service';
import CourseList from '@jobseeker/lotus/components/JobList/V2';
import {AdsElementType, AppState, Course} from '@jobseeker/common-core/types';
import {Card, Typography, useMediaQuery, useTheme} from '@mui/material';
import cookie from 'cookie';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import {
  gladiatorService,
  jobfeedService,
  userService,
} from '@jobseeker/common-core/services';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import MavenWidget from '@jobseeker/feature-profile/components/MavenWidget';
import {useEffect, useState} from 'react';
import {MavenLogo} from '@jobseeker/lotus/assets/images';
import {loadSEO} from '@helpers';
import {useRouter} from 'next/router';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {
  categoryOptions,
  experienceOptions,
  methodOptions,
} from '@jobseeker/feature-course/constants';
import qs from 'qs';
import Head from 'next/head';

type CoursesListProps = {
  courses: Course[] | [];
  filters: {
    page: number;
    loc: string[];
  };
  seoConfig: object;
};
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const CoursesList = ({courses, filters, seoConfig}: CoursesListProps) => {
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, JOBFEED);

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  const {searchOverlay: isSearchEnabled} = useSelector(
    (state: AppState) => state?.app?.layout,
    shallowEqual
  );
  const theme = useTheme();

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const isLarge = useIsLargeDevice();
  const isTabDevice = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const [isMavenOpen, setShowMaven] = useState(false);
  const [isMavenSubmitSuccess, setMavenSubmit] = useState(false);
  const {
    profile: {showMaven},
  } = useSelector((state: AppState) => state.user);
  const onMavenSubmit = async (formData: any) => {
    const respData: any = await userService.updateDiversity(formData);
    if (respData.message) {
      // enqueueSnackbar(respData.message, {variant: 'success'});
      localStorage.setItem('shownMaven', 'true');
      // setShowMaven(false);
      setMavenSubmit(true);
    }
  };

  useEffect(() => {
    const haveShownMaven = Boolean(localStorage.getItem('shownMaven'));
    // const mavenVisibility = true && !haveShownMaven;
    const mavenVisibility = !!(showMaven && !haveShownMaven);
    setShowMaven(mavenVisibility);
  }, [showMaven]);
  const breadcrumbElements = isAppIimjobs
    ? [
        {
          name: 'Home',
          item: '/',
        },
        {
          name: 'Courses',
          item: '',
        },
      ]
    : [];

  const router = useRouter();

  useEffect(() => {
    TrackEventUBA('courseListingPageView', 'view', {
      ref: router.query.ref || '',
      filterCategory: getKeyFromPath('category') || '',
      filterExp: getKeyFromPath('experience') || '',
      filterMethod: getKeyFromPath('method') || '',
      pageName: 'Courses List Page',
      sessionUsername: `${sessionStorage.getItem('userId')}`,
    });
  }, []);

  const courseListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: courses.map(courseInfo => ({
      '@type': 'Course',
      name: courseInfo.title || '',
      description: courseInfo.oneLiner || '',
      url: `${process.env.NEXT_PUBLIC_LOAD_CONFIG}/course/${cleanUrl(
        courseInfo.title
      )}-${courseInfo.id}`,
      provider: {
        '@type': 'Organization',
        name: courseInfo.institute,
      },
    })),
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{__html: JSON.stringify(courseListSchema)}}
        />
      </Head>
      <Interceptor metaInfo={metaData}>
        <NavLayout>
          <ResponsiveLayout
            breadcrumbElements={courses.length > 0 ? breadcrumbElements : []}
            variant={VersionVariants.VERSION_TWO}
          >
            <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
            <SeoLayout seoConfig={seoConfig} />
            {isAppIimjobs ? (
              <>
                {/* {isCSR() && (
                  <SeoIntrup
                    title="IIM Courses for Working Professionals | Leadership & Management Skills for IT Professionals | IIMJobs"
                    description="Browse details about top management courses from IIMs, XLRI, SPJIMR and other top management colleges to learn leadership and management skills."
                    pageNo={0}
                    nextPage={false}
                    type={COURSE}
                  />
                )} */}
                {courses.length === 0 ? (
                  <Typography>No courses found</Typography>
                ) : (
                  <Card
                    sx={{
                      boxShadow: 2,
                      borderRadius: 4,
                      mx: 0,
                      maxWidth: 1160,
                      ml: 'auto',
                      mr: 'auto',
                    }}
                  >
                    <Typography
                      component="h1"
                      display="flex"
                      fontSize={{xxs: 16, md: 20}}
                      fontWeight={{xxs: 600, md: 500}}
                      m={2}
                      mt={3}
                      sx={{
                        wordWrap: 'break-word',
                        textTransform: 'capitalize',
                      }}
                    >
                      Course List
                    </Typography>
                    {courses?.map(
                      ({
                        id,
                        path,
                        title,
                        location,
                        institute,
                        maxWorkExp,
                        minWorkExp,
                      }) => (
                        <CourseList
                          onJobClick={() => {
                            TrackEventUBA('courseClick', 'click', {
                              ref: router.query.ref || '',
                              page: 'CourseListing',
                              sessionUsername: `${sessionStorage.getItem(
                                'userId'
                              )}`,
                              courseRefCode: router.query.ref || '',
                              courseId: id || '',
                            });
                          }}
                          key={id}
                          title={title}
                          variant={VersionVariants.VERSION_TWO}
                          location={
                            location
                              ?.filter(({name}) => name?.length)
                              ?.map(({name}) => name)
                              .join('/') || 'Remote'
                          }
                          companyName={institute}
                          experiance={`${minWorkExp} - ${maxWorkExp} yrs`}
                          date={Date.now()}
                          refCode=""
                          companyImgUrl={path}
                          showPosted={false}
                          isCompanyJob={false}
                          isPremiumJob={false}
                          isSaved={false}
                          showSaveIcon={false}
                          redirectionLink={`/course/${cleanUrl(
                            title.replace(/&/g, '-and-')
                          )}-${id}?refinv=courselist&reforg=jobemail`}
                          whatFor="course"
                        />
                      )
                    )}
                  </Card>
                )}
              </>
            ) : (
              <>
                <JobFeed
                  jobFeedType={'Courses'}
                  catOrTagId={''}
                  catOrTagTitle={'Courses'}
                  showProfileCard={isTabDevice ? false : true}
                  showFeatured={true}
                  showLearning={true}
                  showLearningCenterCTA={true}
                  preFilters={filters}
                  courses={courses}
                />
                {isLarge && isAppIimjobs && (
                  <MavenWidget
                    show={isMavenOpen}
                    imgUrl={MavenLogo}
                    onClose={() => setShowMaven(false)}
                    onSubmit={onMavenSubmit}
                    isSuccess={isMavenSubmitSuccess}
                  />
                )}
              </>
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: COURSE},
    });
    const isBotUser = isUserAgentForBot(context);

    const {isMobile} = utilServerSideDeviceDetection(context);

    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    let userId = -1;
    let token = '';

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          userId = parseInt(validateInfo?.userId ?? '0', 10);
          token = validateInfo?.token ?? '';
        }
      }
    } catch (err) {}
    const pageNo = parseInt(context.query?.page) || 0;
    const minExp = parseInt(context.query?.minexp) || 0;
    const maxExp = parseInt(context.query?.maxexp) || 0;
    const query = parseInt(context.query?.maxexp) || 0;
    const sort = context.query?.sort || null;
    const location = getLocationIds(context.query?.loc || '');
    if (!isAppIimjobs) {
      const blogs = await jobfeedService.getLatestBlogs('');
      const bloData = (Object.values(blogs) as AdsElementType[])
        ?.map(blog => {
          return {
            link: blog?.link,
            title: blog?.title,
            imageUrl: blog?.featuredImage ?? '',
          };
        })
        ?.slice(0, 3);

      store.dispatch({
        type: 'Ads/set_blog_data',
        payload: bloData,
      });
    }
    if (!isAppIimjobs) {
      const category = parseInt(context.query?.category) || -1;
      const method = parseInt(context.query?.method) || -1;
      const exp = context.query?.experience;

      if (category >= 0 || method >= 0 || (minExp > 0 && maxExp > 0 && !exp)) {
        let catName = '';
        let methodName = '';
        let exp = '';

        if (minExp >= 0 && maxExp >= 0) {
          exp =
            experienceOptions.find(
              e => e.minExp == minExp && e.maxExp == maxExp
            )?.name || '';
        }
        if (category >= 0) {
          catName = categoryOptions.find(({id}) => id === category)?.name || '';
        }
        if (method >= 0) {
          methodName = methodOptions.find(({id}) => id === method)?.name || '';
        }

        const updatedQuery = {
          ...context.query,
          ...(catName?.length > 0 ? {category: catName} : {}),
          ...(methodName?.length > 0 ? {method: methodName} : {}),
          ...(exp?.length > 0
            ? {experience: exp}
            : {experience: experienceOptions[0].name}),
        };
        const queryString = qs.stringify(updatedQuery);
        return {
          redirect: {
            destination: `${context.resolvedUrl.split('?')[0]}?${queryString}`,
            permanent: false,
          },
        };
      }
    }
    const locationIds = location
      ? location?.split(',')?.map(v => parseInt(v))
      : null;

    const filters = {
      sort: sort ? [sort] : null,
      page: pageNo > 0 ? pageNo - 1 : 0,
      query: query,
      minexp: minExp,
      maxexp: maxExp,
      loc: locationIds,
    };
    const seoConfig = await loadSEO(COURSE);

    let courses = [];
    if (isBotUser && !isAppIimjobs) {
      const resp = await courseService.getCourses({
        userId: Number(userId),
        token: token || '',
        headers: {
          Origin: process.env.NEXT_PUBLIC_LOAD_CONFIG,
          Referer: process.env.NEXT_PUBLIC_LOAD_CONFIG,
        },
        params: {
          method: 0,
          catId: 0,
          minExp: 0,
          maxExp: 30,
        },
      });
      courses = resp.doc;
    } else {
      if (isAppIimjobs) {
        courses =
          (
            await courseService.getCourses({
              userId,
              token,
              headers: {
                Origin: process.env.NEXT_PUBLIC_LOAD_CONFIG,
                Referer: process.env.NEXT_PUBLIC_LOAD_CONFIG,
              },
            })
          )?.doc ?? [];
      }
    }

    return {
      props: {courses, filters, seoConfig},
    };
  }
);

export default CoursesList;
